import { suggestEmailCorrection, isNonDeliverableEmail } from "./inputValidation";

// Pure, relay-free half of the shared email-validation util (split out of
// useEmailValidation.ts, B2 2026-08-23): kept relay-free on purpose so it's
// importable from the CRA jest harness (client/scripts/test.js), which runs
// tests through a bare babel-preset-react-app transform with no
// babel-plugin-relay — the same config/jest/babelTransform.js every other
// client suite runs under (babelrc/configFile both disabled, so package.json's
// "relay" babel plugin never loads). Importing anything that evaluates a
// react-relay `graphql` template tag at module scope throws
// "Unexpected invocation at runtime" under that harness. See
// useEmailValidation.ts for the debounced live-check half, which DOES use
// react-relay and is exercised at the HTTP surface by the server's own
// emailValidationResolver.test.ts instead.

export type EmailValidationStatus = "valid" | "invalid" | "unknown";

export type EmailValidationReason =
  | "syntax"
  | "junk_domain"
  | "typo_domain"
  | "no_mx"
  | null;

export interface EmailValidationVerdict {
  status: EmailValidationStatus;
  suggestion: string | null;
  reason: EmailValidationReason;
}

export const UNKNOWN_VERDICT: EmailValidationVerdict = {
  status: "unknown",
  suggestion: null,
  reason: null,
};

// Plain copy for a server-confirmed definitive negative (syntax / junk
// domain / no MX) on the quote funnel — no em dashes. Shared by
// QuotePersonalDetailsForm's Form.Item rule and StepContact's defensive
// message.error so the two copies can't drift. A typo-domain verdict is
// informational (ok stays true server-side) and never reaches this message;
// it only drives the "Did you mean" hint.
export const EMAIL_UNDELIVERABLE_MESSAGE =
  "We can't deliver email to this address. Please check for typos.";

/**
 * Instant, offline hint for an email address — same local checks the quote
 * funnel form has always run. No network call.
 */
export function localEmailHint(email: string): {
  suggestion: string | null;
  nonDeliverable: boolean;
} {
  const trimmed = (email || "").trim();
  if (!trimmed) return { suggestion: null, nonDeliverable: false };
  return {
    suggestion: suggestEmailCorrection(trimmed),
    nonDeliverable: isNonDeliverableEmail(trimmed),
  };
}

/**
 * Shared non-blocking (`warningOnly`) antd Form rule for a low-volume admin
 * email field — InviteModal.tsx and MoveQuoteModal.tsx both use this exact
 * rule verbatim rather than hand-rolling their own copy. Deliberately
 * local-only (no live endpoint call) per the B2 build note: these are
 * low-volume staff tools where the offline typo-map + non-deliverable-domain
 * check is enough signal without the added complexity of wiring the
 * debounced remote check (useEmailValidation.ts).
 */
export const localEmailHintRule = {
  warningOnly: true,
  validator: async (_: unknown, value: string) => {
    if (!value) return Promise.resolve();
    const hint = localEmailHint(value);
    if (hint.suggestion) {
      return Promise.reject(new Error(`Did you mean ${hint.suggestion}?`));
    }
    if (hint.nonDeliverable) {
      return Promise.reject(new Error("That email domain isn't accepted."));
    }
    return Promise.resolve();
  },
};
